<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import PipelinesAPI from 'dashboard/api/pipelines';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  pipeline: { type: Object, required: true },
});
const emit = defineEmits(['close', 'deleted', 'updated']);

const store = useStore();

const mode = ref('current'); // 'current' | 'specific' | 'all'
const branchesToRemove = ref([]); // ids selected in 'specific' mode
const confirmName = ref('');
const saving = ref(false);
const errorMsg = ref(null);

const allBranches = computed(() => store.getters['branches/getBranches'] || []);
const activeBranch = computed(() => store.getters['branches/getActiveBranch']);

// IDs donde el pipeline está actualmente activo (resolviendo el "global" semantic)
const currentScopeIds = computed(() => {
  const scope = props.pipeline.scope_branch_ids || [];
  if (!scope.length) return allBranches.value.map(b => b.id); // global → todas
  return [...scope];
});

const activeBranchIds = computed(() => currentScopeIds.value);

// Sucursales donde el pipeline aparece (para mostrar en modo 'specific')
const visibleBranches = computed(() =>
  allBranches.value.filter(b => activeBranchIds.value.includes(b.id))
);

function toggleBranchToRemove(id) {
  const idx = branchesToRemove.value.indexOf(id);
  if (idx === -1) branchesToRemove.value.push(id);
  else branchesToRemove.value.splice(idx, 1);
}

// Cálculo del nuevo scope_branch_ids tras la operación
const newScopeIds = computed(() => {
  if (mode.value === 'current') {
    return activeBranchIds.value.filter(id => id !== activeBranch.value?.id);
  }
  if (mode.value === 'specific') {
    return activeBranchIds.value.filter(id => !branchesToRemove.value.includes(id));
  }
  return []; // 'all' → no aplica (se borra)
});

// Detección: ¿la operación dejaría 0 sucursales? (debería ir por modo 'all')
const willOrphanAll = computed(() => {
  if (mode.value === 'all') return false;
  if (mode.value === 'current') return newScopeIds.value.length === 0;
  if (mode.value === 'specific') return branchesToRemove.value.length > 0 && newScopeIds.value.length === 0;
  return false;
});

const branchNameById = (id) => allBranches.value.find(b => b.id === id)?.name || `#${id}`;

const summaryText = computed(() => {
  if (mode.value === 'current') {
    if (!activeBranch.value) return 'Sin sucursal activa';
    return `El embudo se quitará de "${activeBranch.value.name}". Seguirá disponible en ${newScopeIds.value.length} sucursal(es).`;
  }
  if (mode.value === 'specific') {
    if (!branchesToRemove.value.length) return 'Selecciona al menos una sucursal a remover';
    const names = branchesToRemove.value.map(branchNameById).join(', ');
    return `Se quitará de: ${names}. Seguirá disponible en ${newScopeIds.value.length} sucursal(es).`;
  }
  return `Se eliminará el embudo, sus etapas y los leads quedarán huérfanos en TODAS las sucursales.`;
});

const confirmButtonText = computed(() => {
  if (mode.value === 'current') return `→ Quitar de ${activeBranch.value?.name || 'mi sucursal'}`;
  if (mode.value === 'specific') {
    const n = branchesToRemove.value.length;
    return n === 0 ? '→ Selecciona sucursales' : `→ Quitar de ${n} sucursal${n > 1 ? 'es' : ''}`;
  }
  return '🗑 Eliminar definitivamente';
});

const confirmDisabled = computed(() => {
  if (saving.value) return true;
  if (mode.value === 'current') return !activeBranch.value || activeBranchIds.value.length === 0 || !activeBranchIds.value.includes(activeBranch.value.id);
  if (mode.value === 'specific') return branchesToRemove.value.length === 0;
  return confirmName.value.trim() !== (props.pipeline.name || '').trim();
});

onMounted(() => {
  store.dispatch('branches/get');
});

async function confirm() {
  if (confirmDisabled.value) return;
  if (willOrphanAll.value) {
    // Pasar al modo 'all' automáticamente
    mode.value = 'all';
    return;
  }
  saving.value = true;
  errorMsg.value = null;
  try {
    if (mode.value === 'all') {
      await PipelinesAPI.delete(props.pipeline.id);
      emit('deleted', props.pipeline.id);
    } else {
      const { data } = await PipelinesAPI.update(props.pipeline.id, {
        pipeline: { scope_branch_ids: newScopeIds.value },
      });
      emit('updated', data);
    }
  } catch (e) {
    errorMsg.value = e.response?.data?.message || e.message;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="dpd-overlay" @click.self="emit('close')">
    <div class="dpd-modal" role="dialog" aria-modal="true">
      <!-- HEAD -->
      <div class="dpd-head">
        <div class="dpd-title-wrap">
          <div class="dpd-title-icon">🗑</div>
          <div>
            <div class="dpd-title">Eliminar embudo</div>
            <div class="dpd-subtitle">
              "{{ pipeline.name }}" · {{ pipeline.pipeline_stages?.length || pipeline.stages?.length || 0 }} etapas · {{ activeBranchIds.length }} sucursal(es)
            </div>
          </div>
        </div>
        <button class="dpd-close" type="button" @click="emit('close')" aria-label="Cerrar">×</button>
      </div>

      <!-- BODY -->
      <div class="dpd-body">
        <div class="dpd-question">¿<strong>Cómo</strong> quieres eliminar este embudo?</div>

        <!-- 3 OPCIONES RADIO -->
        <div class="dpd-opt-list">
          <!-- A · Solo de mi sucursal actual -->
          <div
            class="dpd-opt-card"
            :class="{ 'is-selected': mode === 'current', 'is-soft': mode === 'current' }"
            @click="mode = 'current'"
          >
            <div class="dpd-opt-radio"><div class="dpd-opt-radio-dot"></div></div>
            <div class="dpd-opt-icon">🎯</div>
            <div class="dpd-opt-info">
              <div class="dpd-opt-title">Solo de mi sucursal actual</div>
              <div class="dpd-opt-desc">
                Quita el embudo SOLO de <strong>{{ activeBranch?.name || '—' }}</strong>. Seguirá disponible en las otras sucursales.
              </div>
              <div class="dpd-opt-tag soft">Recomendado · seguro</div>
            </div>
          </div>

          <!-- B · De algunas sucursales específicas -->
          <div
            class="dpd-opt-card"
            :class="{ 'is-selected': mode === 'specific', 'is-medium': mode === 'specific' }"
            @click="mode = 'specific'"
          >
            <div class="dpd-opt-radio"><div class="dpd-opt-radio-dot"></div></div>
            <div class="dpd-opt-icon">🏢</div>
            <div class="dpd-opt-info">
              <div class="dpd-opt-title">De algunas sucursales específicas</div>
              <div class="dpd-opt-desc">
                Selecciona en qué sucursales quitar el embudo. Las que NO selecciones lo conservarán.
              </div>
              <div class="dpd-opt-tag medium">Avanzado · selección múltiple</div>

              <!-- Multi-checkbox inline -->
              <div v-if="mode === 'specific'" class="dpd-branches-grid">
                <div
                  v-for="b in visibleBranches"
                  :key="b.id"
                  class="dpd-br-row"
                  :class="{ 'is-on': branchesToRemove.includes(b.id) }"
                  @click.stop="toggleBranchToRemove(b.id)"
                >
                  <div class="dpd-br-cb">
                    <span v-if="branchesToRemove.includes(b.id)">✓</span>
                  </div>
                  <div class="dpd-br-icon">{{ b.code || b.name?.substring(0, 4).toUpperCase() }}</div>
                  <div class="dpd-br-info">
                    <div class="dpd-br-name">{{ b.name }}</div>
                    <div class="dpd-br-meta">
                      {{ b.id === activeBranch?.id ? 'Tu sucursal activa' : (b.is_personal ? 'Personal' : 'Sucursal') }}
                    </div>
                  </div>
                </div>
                <div v-if="visibleBranches.length === 0" class="dpd-empty-msg">
                  El embudo no está activo en ninguna sucursal visible.
                </div>
              </div>
            </div>
          </div>

          <!-- C · DEFINITIVAMENTE de TODAS -->
          <div
            class="dpd-opt-card"
            :class="{ 'is-selected': mode === 'all' }"
            @click="mode = 'all'"
          >
            <div class="dpd-opt-radio"><div class="dpd-opt-radio-dot"></div></div>
            <div class="dpd-opt-icon">💀</div>
            <div class="dpd-opt-info">
              <div class="dpd-opt-title">DEFINITIVAMENTE de TODAS las sucursales</div>
              <div class="dpd-opt-desc">
                El embudo, sus etapas y leads se eliminan permanentemente. <strong>Esta acción NO se puede deshacer.</strong>
              </div>
              <div class="dpd-opt-tag danger">⚠ Irreversible · borra todo</div>

              <!-- Confirmación con input inline -->
              <div v-if="mode === 'all'" class="dpd-confirm-input-wrap">
                <div class="dpd-confirm-label">
                  Para confirmar, escribe <strong>{{ pipeline.name }}</strong> exactamente:
                </div>
                <input
                  v-model="confirmName"
                  class="dpd-confirm-input"
                  placeholder="Escribe el nombre del embudo…"
                  @click.stop
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Resumen de la acción -->
        <div v-if="mode !== 'all' || confirmName" class="dpd-summary" :class="{ 'is-danger': mode === 'all' }">
          {{ summaryText }}
        </div>

        <div v-if="willOrphanAll" class="dpd-warn">
          ⚠ Esta operación dejaría el embudo sin ninguna sucursal · si haces click se cambiará a "Eliminar definitivamente".
        </div>

        <div v-if="errorMsg" class="dpd-error">⚠ {{ errorMsg }}</div>
      </div>

      <!-- FOOT -->
      <div class="dpd-foot">
        <button @click="emit('close')" class="dpd-btn dpd-cancel" type="button">Cancelar</button>
        <button @click="confirm" :disabled="confirmDisabled" class="dpd-btn dpd-delete" type="button">
          {{ saving ? '⏳ Procesando…' : confirmButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
/* DeletePipelineDialog · D2 · cristal negro + rojo */
.dpd-overlay { position: fixed; inset: 0; z-index: 50; background: rgba(0,0,0,0.78); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 24px; font-family: -apple-system, system-ui, sans-serif; }
.dpd-modal { width: 580px; max-width: 100%; max-height: calc(100vh - 48px); background: rgba(10,10,10,0.98); border: 1.5px solid rgba(239,68,68,0.45); border-radius: 18px; overflow: hidden; box-shadow: 0 30px 90px rgba(0,0,0,0.85), 0 0 50px rgba(239,68,68,0.18); display: flex; flex-direction: column; position: relative; color: #fff; }
.dpd-modal::before { content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: 18px; background: linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.04) 22%, transparent 24%), radial-gradient(circle at 100% 0%, rgba(239,68,68,0.10), transparent 60%); z-index: 1; }
.dpd-modal > * { position: relative; z-index: 2; }

.dpd-head { padding: 18px 22px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: space-between; align-items: center; background: radial-gradient(ellipse at 0% 0%, rgba(239,68,68,0.10), transparent 60%); flex-shrink: 0; }
.dpd-title-wrap { display: flex; align-items: center; gap: 12px; }
.dpd-title-icon { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, rgba(239,68,68,0.20), rgba(220,38,38,0.10)); border: 1.5px solid rgba(239,68,68,0.40); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.dpd-title { font-size: 18px; font-weight: 900; color: #fff; line-height: 1.15; }
.dpd-subtitle { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.dpd-close { width: 28px; height: 28px; border-radius: 50%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 16px; line-height: 1; padding: 0; font-family: inherit; transition: all 0.15s; }
.dpd-close:hover { background: rgba(239,68,68,0.20); color: #fff; border-color: rgba(239,68,68,0.40); transform: rotate(90deg); }

.dpd-body { padding: 18px 22px; flex: 1; overflow-y: auto; }
.dpd-question { font-size: 12px; color: rgba(255,255,255,0.85); margin-bottom: 14px; line-height: 1.5; }
.dpd-question strong { color: #f87171; }

.dpd-opt-list { display: flex; flex-direction: column; gap: 10px; }
.dpd-opt-card { padding: 14px 16px; background: rgba(255,255,255,0.03); border: 2px solid rgba(255,255,255,0.08); border-radius: 12px; cursor: pointer; transition: all 0.18s; display: flex; gap: 14px; align-items: flex-start; }
.dpd-opt-card:hover { border-color: rgba(255,255,255,0.20); transform: translateY(-1px); }
.dpd-opt-card.is-selected { background: linear-gradient(135deg, rgba(239,68,68,0.10), rgba(220,38,38,0.04)); border-color: rgba(239,68,68,0.55); box-shadow: 0 0 24px rgba(239,68,68,0.20); }
.dpd-opt-card.is-selected.is-soft { background: linear-gradient(135deg, rgba(99,102,241,0.10), rgba(67,56,202,0.04)); border-color: rgba(99,102,241,0.55); box-shadow: 0 0 24px rgba(99,102,241,0.20); }
.dpd-opt-card.is-selected.is-medium { background: linear-gradient(135deg, rgba(251,191,36,0.10), rgba(217,119,6,0.04)); border-color: rgba(251,191,36,0.55); box-shadow: 0 0 24px rgba(251,191,36,0.20); }

.dpd-opt-radio { width: 22px; height: 22px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.25); flex-shrink: 0; margin-top: 2px; display: flex; align-items: center; justify-content: center; }
.dpd-opt-card.is-selected .dpd-opt-radio { border-color: #ef4444; }
.dpd-opt-card.is-selected.is-soft .dpd-opt-radio { border-color: #818cf8; }
.dpd-opt-card.is-selected.is-medium .dpd-opt-radio { border-color: #fbbf24; }
.dpd-opt-radio-dot { width: 10px; height: 10px; border-radius: 50%; background: transparent; }
.dpd-opt-card.is-selected .dpd-opt-radio-dot { background: #ef4444; }
.dpd-opt-card.is-selected.is-soft .dpd-opt-radio-dot { background: #818cf8; }
.dpd-opt-card.is-selected.is-medium .dpd-opt-radio-dot { background: #fbbf24; }

.dpd-opt-icon { width: 38px; height: 38px; border-radius: 10px; background: rgba(255,255,255,0.04); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.dpd-opt-card.is-selected .dpd-opt-icon { background: rgba(239,68,68,0.18); }
.dpd-opt-card.is-selected.is-soft .dpd-opt-icon { background: rgba(99,102,241,0.18); }
.dpd-opt-card.is-selected.is-medium .dpd-opt-icon { background: rgba(251,191,36,0.18); }

.dpd-opt-info { flex: 1; min-width: 0; }
.dpd-opt-title { font-size: 14px; font-weight: 900; color: #fff; }
.dpd-opt-card.is-selected.is-soft .dpd-opt-title { color: #818cf8; }
.dpd-opt-card.is-selected.is-medium .dpd-opt-title { color: #fbbf24; }
.dpd-opt-card.is-selected:not(.is-soft):not(.is-medium) .dpd-opt-title { color: #f87171; }
.dpd-opt-desc { font-size: 11px; color: rgba(255,255,255,0.65); margin-top: 4px; line-height: 1.5; }
.dpd-opt-desc strong { color: #fff; }

.dpd-opt-tag { display: inline-block; padding: 3px 8px; border-radius: 5px; font-size: 9px; font-weight: 800; letter-spacing: 0.5px; margin-top: 6px; text-transform: uppercase; }
.dpd-opt-tag.soft { background: rgba(99,102,241,0.15); color: #818cf8; border: 1px solid rgba(99,102,241,0.40); }
.dpd-opt-tag.medium { background: rgba(251,191,36,0.15); color: #fbbf24; border: 1px solid rgba(251,191,36,0.40); }
.dpd-opt-tag.danger { background: rgba(239,68,68,0.15); color: #f87171; border: 1px solid rgba(239,68,68,0.40); }

/* Sub-flow B · multi-checkbox de sucursales */
.dpd-branches-grid { display: flex; flex-direction: column; gap: 5px; margin-top: 12px; }
.dpd-br-row { display: flex; align-items: center; gap: 10px; padding: 9px 12px; background: rgba(255,255,255,0.03); border: 1.5px solid rgba(255,255,255,0.08); border-radius: 8px; cursor: pointer; transition: all 0.15s; }
.dpd-br-row:hover { border-color: rgba(239,68,68,0.30); }
.dpd-br-row.is-on { background: linear-gradient(135deg, rgba(239,68,68,0.10), rgba(220,38,38,0.04)); border-color: rgba(239,68,68,0.55); box-shadow: 0 0 12px rgba(239,68,68,0.18); }
.dpd-br-cb { width: 20px; height: 20px; border-radius: 5px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.18); flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #fff; font-weight: 900; }
.dpd-br-row.is-on .dpd-br-cb { background: linear-gradient(135deg, #ef4444, #dc2626); border-color: #ef4444; }
.dpd-br-icon { width: 28px; height: 28px; border-radius: 6px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 900; color: #fbbf24; flex-shrink: 0; letter-spacing: 0.3px; }
.dpd-br-row.is-on .dpd-br-icon { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.40); color: #f87171; }
.dpd-br-info { flex: 1; min-width: 0; }
.dpd-br-name { font-size: 12px; font-weight: 800; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dpd-br-row.is-on .dpd-br-name { color: #f87171; text-decoration: line-through; text-decoration-color: rgba(239,68,68,0.50); }
.dpd-br-meta { font-size: 10px; color: rgba(255,255,255,0.45); margin-top: 1px; }
.dpd-empty-msg { padding: 12px; font-size: 11px; color: rgba(255,255,255,0.45); text-align: center; font-style: italic; }

/* Sub-flow C · input de confirmación */
.dpd-confirm-input-wrap { margin-top: 12px; }
.dpd-confirm-label { font-size: 11px; color: rgba(255,255,255,0.65); margin-bottom: 6px; }
.dpd-confirm-label strong { color: #f87171; font-family: ui-monospace, monospace; padding: 2px 6px; background: rgba(239,68,68,0.10); border-radius: 4px; }
.dpd-confirm-input { width: 100%; padding: 10px 12px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(239,68,68,0.40); border-radius: 7px; color: #fff; font-size: 13px; font-family: ui-monospace, monospace; outline: none; }
.dpd-confirm-input:focus { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.15); }
.dpd-confirm-input::placeholder { color: rgba(255,255,255,0.30); }

/* Resumen + warn + error */
.dpd-summary { margin-top: 14px; padding: 11px 14px; background: rgba(239,68,68,0.06); border: 1px dashed rgba(239,68,68,0.40); border-radius: 9px; font-size: 11px; color: #f87171; line-height: 1.5; }
.dpd-summary.is-danger { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.55); border-style: solid; }
.dpd-warn { margin-top: 10px; padding: 10px 12px; background: rgba(245,158,11,0.10); border: 1px solid rgba(245,158,11,0.40); border-radius: 8px; font-size: 11px; color: #fbbf24; line-height: 1.5; }
.dpd-error { margin-top: 14px; padding: 10px 12px; background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.40); border-radius: 8px; font-size: 11px; color: #f87171; font-weight: 700; }

.dpd-foot { padding: 14px 22px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: flex-end; gap: 8px; background: rgba(0,0,0,0.4); flex-shrink: 0; }
.dpd-btn { padding: 10px 18px; border-radius: 8px; font-size: 12px; font-weight: 800; cursor: pointer; border: none; font-family: inherit; }
.dpd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.dpd-cancel { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.08); }
.dpd-cancel:hover { background: rgba(255,255,255,0.08); }
.dpd-delete { background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff; box-shadow: 0 0 16px rgba(239,68,68,0.30); }
.dpd-delete:hover:not(:disabled) { box-shadow: 0 0 24px rgba(239,68,68,0.55); }
</style>
